// src/components/Tasker/TaskerServiceOfferingEdit.js
// ✅ Updates requested:
// - All fields mandatory except Description
// - preventDefault on submit
// - inline errors: "This <field> is mandatory"
// - success alert on submit (auto dismiss)
// - still: ownership check + save + sync taskers categories/subcategories (2-way)

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Trash2, Eye, EyeOff } from "lucide-react";

import { Button } from "../ui/Button";
import useCategories from "../../hooks/useCategories";
import { useAuth } from "../../contexts/AuthContext";
import { db, auth } from "../../firebase";

import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { syncTaskerTaxonomyFromActiveOfferings } from "./TaskerDashboard";

export default function TaskerServiceOfferingEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  const { categories } = useCategories();
  const { profile } = useAuth();

  const taskerId = profile?.uid || auth?.currentUser?.uid || null;

  const [loading, setLoading] = useState(true);
  const [notAllowed, setNotAllowed] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState(""); // optional
  const [price, setPrice] = useState("");
  const [status, setStatus] = useState("active"); // active | in-active | deleted

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null); // { type: "success"|"error", message }

  const category = useMemo(
    () => (categories || []).find((c) => c.id === selectedCategory),
    [categories, selectedCategory]
  );

  const scrollToTop = () => {
  // scroll the page to top so the alert is visible
  window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const showToast = (type, message) => {
  scrollToTop(); // ✅ ensure alert is visible
  setToast({ type, message });
  window.clearTimeout(showToast._t);
  showToast._t = window.setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!id) return;

      if (!taskerId) {
        setLoading(false);
        setNotAllowed(true);
        return;
      }

      try {
        const ref = doc(db, "serviceOfferings", id);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          if (!mounted) return;
          setLoading(false);
          showToast("error", "Service offering not found.");
          return;
        }

        const data = snap.data() || {};

        // ownership
        if ((data?.taskerId || null) !== taskerId) {
          if (!mounted) return;
          setNotAllowed(true);
          setLoading(false);
          return;
        }

        if (!mounted) return;

        setSelectedCategory(data?.categoryId || "");
        setSelectedSubcategories(Array.isArray(data?.subcategories) ? data.subcategories : []);
        setTitle(data?.title || "");
        setDescription(data?.description || "");
        setPrice(
          typeof data?.price === "number"
            ? String(data.price)
            : data?.price != null
            ? String(data.price)
            : ""
        );
        setStatus(data?.status || "active");

        setLoading(false);
      } catch (e) {
        console.error(e);
        if (!mounted) return;
        setLoading(false);
        showToast("error", "Failed to load offering.");
      }
    }

    load();
    return () => {
      mounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, taskerId]);

  const toggleSubcategory = (subcatId) => {
    setSelectedSubcategories((prev) =>
      prev.includes(subcatId) ? prev.filter((x) => x !== subcatId) : [...prev, subcatId]
    );
  };

  const validate = () => {
    const next = {};

    if (!selectedCategory) next.category = "This Service Category is mandatory";
    if (!selectedSubcategories.length)
      next.subcategories = "This Services You Offer is mandatory";
    if (!title.trim()) next.title = "This Service Title is mandatory";

    // price is mandatory (positive number)
    const p = Number(price);
    if (price === "" || Number.isNaN(p) || p <= 0) next.price = "This Fixed Price (SAR) is mandatory";

    // status is mandatory (active/in-active only)
    const st = status || "";
    if (!st || (st !== "active" && st !== "in-active"))
      next.status = "This Status is mandatory";

    return next;
  };

  const onSave = async (e) => {
    e.preventDefault();

    if (!taskerId || !id) return;

    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
    scrollToTop(); // ✅ show errors/alert area immediately
    showToast("error", "Please fix the highlighted fields.");
    return;
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, "serviceOfferings", id), {
        categoryId: selectedCategory,
        subcategories: selectedSubcategories,
        title: title.trim(),
        description: description?.trim() || "",
        price: Number(price),
        status,
        updatedAt: serverTimestamp(),
      });

      await syncTaskerTaxonomyFromActiveOfferings(db, taskerId);

      showToast("success", "Service offering updated successfully.");
    } catch (e2) {
      console.error(e2);
      showToast("error", "Failed to update offering.");
    } finally {
      setSaving(false);
    }
  };

  const onToggleStatus = async () => {
    if (!taskerId || !id) return;

    const next = (status || "active") === "active" ? "in-active" : "active";
    setStatus(next);
    setErrors((p) => ({ ...p, status: undefined }));

    try {
      await updateDoc(doc(db, "serviceOfferings", id), {
        status: next,
        updatedAt: serverTimestamp(),
      });

      await syncTaskerTaxonomyFromActiveOfferings(db, taskerId);

      showToast("success", next === "active" ? "Service turned on." : "Service turned off.");
    } catch (e) {
      console.error(e);
      showToast("error", "Failed to update status.");
    }
  };

  const onDelete = async () => {
    if (!taskerId || !id) return;

    const ok = window.confirm("Delete this service offering?");
    if (!ok) return;

    try {
      await updateDoc(doc(db, "serviceOfferings", id), {
        status: "deleted",
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await syncTaskerTaxonomyFromActiveOfferings(db, taskerId);

      showToast("success", "Service offering deleted.");
      navigate("/tasker/dashboard");
    } catch (e) {
      console.error(e);
      showToast("error", "Failed to delete offering.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-sm p-6 text-center text-gray-500">
            Loading...
          </div>
        </div>
      </div>
    );
  }

  if (notAllowed) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="text-[#1E3A8A] mb-2">Access denied</div>
            <p className="text-gray-600 mb-4">
              You are not allowed to edit this service offering.
            </p>
            <Link
              to="/tasker/dashboard"
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <ArrowLeft size={16} /> Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isActive = (status || "active") === "active";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={18} />
            Back
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onToggleStatus}
              className={`p-2 rounded border ${
                isActive
                  ? "border-blue-200 text-blue-600 hover:bg-blue-50"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
              title={isActive ? "Turn Off (In-active)" : "Turn On (Active)"}
            >
              {isActive ? <Eye size={18} /> : <EyeOff size={18} />}
            </button>

            <button
              type="button"
              onClick={onDelete}
              className="p-2 rounded border border-red-200 text-red-600 hover:bg-red-50"
              title="Delete"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>

        {/* Alert */}
        {toast && (
          <div
            className={`mb-4 rounded-lg px-4 py-3 text-sm border ${
              toast.type === "success"
                ? "bg-green-50 border-green-200 text-green-700"
                : "bg-red-50 border-red-200 text-red-700"
            }`}
          >
            {toast.message}
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h1 className="text-[#1E3A8A] mb-6">Edit Service Offering</h1>

          <form className="space-y-6" onSubmit={onSave}>
            {/* Category */}
            <div>
              <label className="block text-sm mb-2 text-gray-700">Service Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedSubcategories([]);
                  setErrors((p) => ({
                    ...p,
                    category: undefined,
                    subcategories: undefined,
                  }));
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
              >
                <option value="">Select a category...</option>
                {(categories || []).map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
              </select>
              {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
            </div>

            {/* Subcategories */}
            {selectedCategory && category && (
              <div>
                <label className="block text-sm mb-2 text-gray-700">
                  Services You Offer (Select all that apply)
                </label>

                <div className="grid md:grid-cols-2 gap-3">
                  {(category.subcategories || []).map((subcat) => (
                    <label
                      key={subcat.id}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedSubcategories.includes(subcat.id)
                          ? "border-[#006C35] bg-green-50"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSubcategories.includes(subcat.id)}
                        onChange={() => {
                          toggleSubcategory(subcat.id);
                          setErrors((p) => ({ ...p, subcategories: undefined }));
                        }}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm">{subcat.name}</div>
                        <div className="text-xs text-gray-500">{subcat.description}</div>
                      </div>
                    </label>
                  ))}
                </div>

                {errors.subcategories && (
                  <p className="mt-1 text-sm text-red-600">{errors.subcategories}</p>
                )}
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm mb-2 text-gray-700">Service Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setErrors((p) => ({ ...p, title: undefined }));
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
              />
              {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
            </div>

            {/* Description (optional) */}
            <div>
              <label className="block text-sm mb-2 text-gray-700">Description (Optional)</label>
              <textarea
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
              />
            </div>

            {/* Fixed Price */}
            <div>
              <label className="block text-sm mb-2 text-gray-700">Fixed Price (SAR)</label>
              <input
                type="number"
                value={price}
                onChange={(e) => {
                  setPrice(e.target.value);
                  setErrors((p) => ({ ...p, price: undefined }));
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
              />
              {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price}</p>}
            </div>

            {/* Status (mandatory) */}
            {/* <div>
              <label className="block text-sm mb-2 text-gray-700">Status</label>
              <select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setErrors((p) => ({ ...p, status: undefined }));
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
              >
                <option value="active">active</option>
                <option value="in-active">in-active</option>
              </select>
              {errors.status && <p className="mt-1 text-sm text-red-600">{errors.status}</p>}
            </div> */}

            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-[#006C35] hover:bg-[#005428] text-white py-6 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Save size={18} className="mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}